import { useEffect, useMemo, useState } from "react";
import {
  AudioLines,
  CheckCircle2,
  Clock3,
  Download,
  FileSpreadsheet,
  FileText,
  PhoneCall,
  PhoneIncoming,
  PhoneMissed,
  PhoneOutgoing,
  Play,
  RefreshCw,
  Search,
} from "lucide-react";
import { api, fmtDate, fmtDuration, translateHangupCause } from "@/lib/api";
import { useAuth } from "@/lib/auth";

const FILTER_DEFAULTS = {
  start: "",
  end: "",
  src: "",
  dst: "",
  did: "",
  extension: "",
  trunk: "",
  direction: "",
  status: "",
  search: "",
};

const REPORT_TABS = [
  ["dashboard", "Visão geral"],
  ["detalhado", "CDR detalhado"],
  ["status", "Status"],
  ["recebidas", "Recebidas"],
  ["realizadas", "Realizadas"],
  ["perdidas", "Perdidas"],
  ["ramais", "Por ramal"],
  ["dids", "Por DID"],
  ["troncos", "Por tronco"],
  ["periodo", "Período"],
  ["duracao", "Duração"],
  ["atendimento", "Atendimento"],
  ["falhas", "Falhas SIP"],
  ["simultaneidade", "Simultaneidade"],
  ["gravacoes", "Gravações"],
];

const STATUS_LABELS = {
  ANSWERED: "Completada",
  "NO ANSWER": "Só chamou",
  BUSY: "Ocupado",
  ABANDONED: "Encerrada pela origem",
  FAILED: "Falhou",
  CONGESTION: "Congestionada",
};

const STATUS_TAGS = {
  ANSWERED: "tag-online",
  "NO ANSWER": "tag-warn",
  BUSY: "tag-warn",
  ABANDONED: "tag-warn",
  FAILED: "tag-danger",
  CONGESTION: "tag-danger",
};

const DIRECTION_LABELS = {
  inbound: "Entrada externa",
  outbound: "Saída externa",
  local: "Interna",
  internal: "Interna",
};

function MiniCard({ icon: Icon, label, value, helper, valueClass = "" }) {
  return (
    <div className="sbc-card p-4 min-w-0">
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-widest text-[color:var(--text-muted)]">{label}</div>
          <div className={`text-2xl font-mono mt-1 truncate ${valueClass}`}>{value}</div>
          {helper ? <div className="text-[11px] text-[color:var(--text-muted)] mt-1">{helper}</div> : null}
        </div>
        <div className="h-10 w-10 rounded-sm border border-[color:var(--border-default)] flex items-center justify-center text-[color:var(--accent-blue)] bg-[color:var(--surface-muted)]">
          <Icon size={18} />
        </div>
      </div>
    </div>
  );
}

function EmptyState({ message = "Nenhum dado encontrado para os filtros selecionados." }) {
  return <div className="py-10 text-center text-xs text-[color:var(--text-muted)]">{message}</div>;
}

function HorizontalBars({ title, rows = [], valueKey = "count", labelKey = "label", helper }) {
  const max = Math.max(1, ...rows.map((row) => Number(row?.[valueKey] || 0)));
  return (
    <section className="sbc-card p-4">
      <div className="flex items-center justify-between gap-3 mb-4">
        <div>
          <h2 className="text-sm font-semibold">{title}</h2>
          {helper ? <div className="text-[11px] text-[color:var(--text-muted)] mt-1">{helper}</div> : null}
        </div>
      </div>
      {rows.length === 0 ? (
        <EmptyState />
      ) : (
        <div className="space-y-3">
          {rows.map((row, index) => {
            const value = Number(row?.[valueKey] || 0);
            const width = Math.max(3, Math.round((value / max) * 100));
            return (
              <div key={`${row?.[labelKey] || "row"}-${index}`} className="space-y-1">
                <div className="flex items-center justify-between gap-3 text-xs">
                  <span className="truncate">{row?.[labelKey] || "-"}</span>
                  <span className="font-mono">{value}</span>
                </div>
                <div className="h-2 rounded-sm bg-[color:var(--surface-muted)] overflow-hidden">
                  <div className="h-full bg-[color:var(--accent-blue)]" style={{ width: `${width}%` }} />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}

function DataTable({ columns, rows, emptyMessage, rowKey }) {
  return (
    <div className="sbc-card overflow-hidden">
      <div className="overflow-auto max-h-[65vh]">
        <table className="sbc-table min-w-[980px]">
          <thead>
            <tr>{columns.map((column) => <th key={column.key}>{column.label}</th>)}</tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={columns.length}><EmptyState message={emptyMessage} /></td>
              </tr>
            ) : rows.map((row, index) => (
              <tr key={rowKey ? rowKey(row, index) : index}>
                {columns.map((column) => (
                  <td key={column.key} className={column.className || ""}>
                    {column.render ? column.render(row) : (row?.[column.key] ?? "-")}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function RecordingActions({ row }) {
  if (!row?.cdr_id) return "-";
  const audioUrl = `${api.defaults.baseURL}/recordings/library/${row.cdr_id}/audio`;
  const downloadUrl = `${audioUrl}?download=true`;
  return (
    <div className="flex items-center gap-2">
      <audio controls preload="none" className="h-8 max-w-[240px]">
        <source src={audioUrl} />
      </audio>
      <a className="sbc-btn h-8 px-2" href={audioUrl} target="_blank" rel="noreferrer" title="Ouvir">
        <Play size={14} />
      </a>
      <a className="sbc-btn h-8 px-2" href={downloadUrl} title="Baixar">
        <Download size={14} />
      </a>
    </div>
  );
}

function useDefaultPeriod() {
  return useMemo(() => {
    const end = new Date();
    const start = new Date(end);
    start.setDate(end.getDate() - 30);
    return {
      start: start.toISOString().slice(0, 10),
      end: end.toISOString().slice(0, 10),
    };
  }, []);
}

export default function CDRPage() {
  const { activeTenantId } = useAuth();
  const defaultPeriod = useDefaultPeriod();
  const [activeTab, setActiveTab] = useState("dashboard");
  const [filters, setFilters] = useState({ ...FILTER_DEFAULTS, ...defaultPeriod });
  const [draft, setDraft] = useState({ ...FILTER_DEFAULTS, ...defaultPeriod });
  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [extensions, setExtensions] = useState([]);
  const [dids, setDids] = useState([]);
  const [trunks, setTrunks] = useState([]);

  const reportParams = (nextFilters = filters) => {
    const params = Object.fromEntries(Object.entries(nextFilters).filter(([, value]) => String(value || "").trim() !== ""));
    if (activeTenantId) params.tenant_id = activeTenantId;
    return params;
  };

  const load = async (nextFilters = filters) => {
    setLoading(true);
    try {
      const { data } = await api.get("/cdr/analytics", { params: reportParams(nextFilters) });
      setPayload(data);
      setError("");
    } catch (err) {
      setError(err.response?.data?.detail || "Falha ao carregar o relatório de ligações.");
    } finally {
      setLoading(false);
    }
  };

  const loadFilterOptions = async () => {
    try {
      const [extensionsResponse, didsResponse, trunksResponse] = await Promise.all([
        api.get("/ramais"),
        api.get("/dids"),
        api.get("/operadoras"),
      ]);
      setExtensions(
        (extensionsResponse.data || [])
          .filter((row) => row?.enabled !== false)
          .sort((a, b) => String(a?.numero || "").localeCompare(String(b?.numero || ""), "pt-BR", { numeric: true })),
      );
      setDids(
        (didsResponse.data || [])
          .filter((row) => row?.enabled !== false)
          .sort((a, b) => String(a?.numero || "").localeCompare(String(b?.numero || ""), "pt-BR", { numeric: true })),
      );
      setTrunks(
        (trunksResponse.data || [])
          .filter((row) => row?.enabled !== false)
          .sort((a, b) => String(a?.nome || a?.host || "").localeCompare(String(b?.nome || b?.host || ""), "pt-BR")),
      );
    } catch (_err) {
      setExtensions([]);
      setDids([]);
      setTrunks([]);
    }
  };

  useEffect(() => {
    const next = { ...FILTER_DEFAULTS, ...defaultPeriod };
    setFilters(next);
    setDraft(next);
    load(next);
    loadFilterOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTenantId]);

  const applyFilters = () => {
    setFilters(draft);
    load(draft);
  };

  const clearFilters = () => {
    const next = { ...FILTER_DEFAULTS, ...defaultPeriod };
    setDraft(next);
    setFilters(next);
    load(next);
  };

  const exportFile = async (format) => {
    const response = await api.get(`/cdr/export.${format}`, { params: reportParams(filters), responseType: "blob" });
    const blob = new Blob([response.data], { type: response.headers["content-type"] || "application/octet-stream" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `relatorio_ligacoes.${format}`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  const summary = payload?.summary || {};
  const detailedRows = payload?.detailed_calls || [];

  const detailColumns = [
    { key: "started_at", label: "Data / Hora", render: (row) => fmtDate(row.started_at), className: "font-mono text-xs" },
    { key: "src", label: "Origem", className: "font-mono" },
    { key: "dst", label: "Destino", className: "font-mono" },
    { key: "did", label: "DID", className: "font-mono text-xs" },
    { key: "trunk_name", label: "Tronco / Operadora" },
    { key: "direction", label: "Direção", render: (row) => DIRECTION_LABELS[row.direction] || row.direction || "-" },
    { key: "duration", label: "Duração total", render: (row) => fmtDuration(row.duration), className: "font-mono" },
    { key: "billsec", label: "Tempo falado", render: (row) => fmtDuration(row.billsec), className: "font-mono text-[color:var(--accent-green)]" },
    { key: "wait_sec", label: "Espera", render: (row) => fmtDuration(row.wait_sec), className: "font-mono" },
    { key: "disposition", label: "Status", render: (row) => <span className={`sbc-tag ${STATUS_TAGS[row.disposition] || "tag-offline"}`}>{STATUS_LABELS[row.disposition] || row.disposition || "-"}</span> },
    { key: "hangup_cause", label: "Encerramento", render: (row) => row.finalization_reason || translateHangupCause(row.hangup_cause) },
    { key: "call_id", label: "UUID / Call-ID", className: "font-mono text-xs" },
    { key: "recording", label: "Gravação", render: (row) => row.recording_path ? <RecordingActions row={row} /> : "-" },
  ];

  const renderTab = () => {
    if (loading && !payload) {
      return <div className="sbc-card p-8 text-center text-[color:var(--text-muted)]">Carregando relatório...</div>;
    }

    switch (activeTab) {
      case "dashboard":
        return (
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-3">
              <MiniCard icon={PhoneCall} label="Total de ligações" value={summary.total_calls || 0} />
              <MiniCard icon={CheckCircle2} label="Atendidas" value={summary.answered || 0} valueClass="text-[color:var(--accent-green)]" />
              <MiniCard icon={PhoneMissed} label="Não atendidas" value={summary.not_answered || 0} valueClass="text-[color:var(--accent-amber)]" />
              <MiniCard icon={PhoneIncoming} label="Recebidas" value={summary.received || 0} />
              <MiniCard icon={PhoneOutgoing} label="Realizadas" value={summary.placed || 0} />
              <MiniCard icon={AudioLines} label="Minutos falados" value={summary.talk_minutes || 0} />
              <MiniCard icon={Clock3} label="Duração média" value={fmtDuration(summary.avg_duration_sec || 0)} />
              <MiniCard icon={Clock3} label="Tempo médio atendimento" value={fmtDuration(summary.avg_answer_sec || 0)} />
              <MiniCard icon={CheckCircle2} label="Taxa de atendimento" value={`${summary.answer_rate || 0}%`} />
              <MiniCard icon={PhoneCall} label="Pico simultâneo" value={summary.peak_simultaneous || 0} />
            </div>
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
              <HorizontalBars title="Atendidas x perdidas" rows={payload?.status_breakdown || []} valueKey="count" labelKey="label" />
              <HorizontalBars title="Entrada x saída por hora" rows={payload?.period_hourly || []} valueKey="count" labelKey="label" />
              <HorizontalBars title="Top 10 ramais" rows={(payload?.extension_ranking || []).slice(0, 10).map((row) => ({ label: row.extension, count: row.total }))} />
              <HorizontalBars title="Top DIDs" rows={(payload?.did_summary || []).slice(0, 10).map((row) => ({ label: row.did, count: row.total }))} />
              <HorizontalBars title="Uso dos troncos" rows={(payload?.trunk_summary || []).slice(0, 10).map((row) => ({ label: row.trunk_name, count: row.calls }))} />
              <HorizontalBars title="Falhas SIP / encerramento" rows={payload?.sip_failures || []} valueKey="count" labelKey="label" />
            </div>
          </div>
        );
      case "detalhado":
        return <DataTable columns={detailColumns} rows={detailedRows} emptyMessage="Nenhuma chamada encontrada." rowKey={(row) => row.cdr_id || row.call_id} />;
      case "status":
        return <DataTable columns={[
          { key: "label", label: "Status" },
          { key: "count", label: "Quantidade", className: "font-mono" },
          { key: "percent", label: "Percentual", render: (row) => `${row.percent || 0}%`, className: "font-mono" },
        ]} rows={payload?.status_breakdown || []} />;
      case "recebidas":
        return <DataTable columns={[
          { key: "did", label: "DID", className: "font-mono" },
          { key: "src", label: "Origem", className: "font-mono" },
          { key: "dst", label: "Ramal / Destino", className: "font-mono" },
          { key: "answered_by", label: "Quem atendeu" },
          { key: "wait_sec", label: "Tempo de espera", render: (row) => fmtDuration(row.wait_sec), className: "font-mono" },
          { key: "duration", label: "Duração", render: (row) => fmtDuration(row.duration), className: "font-mono" },
          { key: "missed", label: "Perdida", render: (row) => row.missed ? "Sim" : "Não" },
        ]} rows={payload?.received_calls || []} />;
      case "realizadas":
        return <DataTable columns={[
          { key: "src", label: "Ramal / Origem", className: "font-mono" },
          { key: "dst", label: "Número chamado", className: "font-mono" },
          { key: "trunk_name", label: "Tronco utilizado" },
          { key: "duration", label: "Duração", render: (row) => fmtDuration(row.duration), className: "font-mono" },
          { key: "answered", label: "Atendida", render: (row) => row.answered ? "Sim" : "Não" },
          { key: "cost_total", label: "Custo", render: (row) => Number(row.cost_total || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" }) },
        ]} rows={payload?.outbound_calls || []} />;
      case "perdidas":
        return <DataTable columns={[
          { key: "src", label: "Origem", className: "font-mono" },
          { key: "did", label: "DID", className: "font-mono" },
          { key: "started_at", label: "Horário", render: (row) => fmtDate(row.started_at), className: "font-mono text-xs" },
          { key: "wait_sec", label: "Tempo aguardado", render: (row) => fmtDuration(row.wait_sec), className: "font-mono" },
          { key: "queue_number", label: "Fila" },
          { key: "destination", label: "Destino", className: "font-mono" },
          { key: "hangup_cause", label: "Motivo", render: (row) => translateHangupCause(row.hangup_cause) },
        ]} rows={payload?.missed_calls || []} />;
      case "ramais":
        return <DataTable columns={[
          { key: "extension", label: "Ramal", className: "font-mono" },
          { key: "total", label: "Total", className: "font-mono" },
          { key: "received", label: "Recebidas", className: "font-mono" },
          { key: "placed", label: "Realizadas", className: "font-mono" },
          { key: "answered", label: "Atendidas", className: "font-mono" },
          { key: "missed", label: "Perdidas", className: "font-mono" },
          { key: "talk_sec", label: "Tempo falado", render: (row) => fmtDuration(row.talk_sec), className: "font-mono" },
          { key: "avg_duration", label: "Duração média", render: (row) => fmtDuration(row.avg_duration), className: "font-mono" },
        ]} rows={payload?.extension_ranking || []} />;
      case "dids":
        return <DataTable columns={[
          { key: "did", label: "DID", className: "font-mono" },
          { key: "total", label: "Quantidade", className: "font-mono" },
          { key: "answered", label: "Atendidas", className: "font-mono" },
          { key: "missed", label: "Perdidas", className: "font-mono" },
          { key: "avg_duration", label: "Duração média", render: (row) => fmtDuration(row.avg_duration), className: "font-mono" },
          { key: "peak_hours", label: "Horários de pico" },
        ]} rows={payload?.did_summary || []} />;
      case "troncos":
        return <DataTable columns={[
          { key: "trunk_name", label: "Tronco / Operadora" },
          { key: "calls", label: "Chamadas", className: "font-mono" },
          { key: "minutes", label: "Minutos", className: "font-mono" },
          { key: "failures", label: "Falhas", className: "font-mono" },
          { key: "success_rate", label: "Taxa de sucesso", render: (row) => `${row.success_rate || 0}%`, className: "font-mono" },
          { key: "peak_simultaneous", label: "Pico simultâneo", className: "font-mono" },
          { key: "cost_total", label: "Custo", render: (row) => Number(row.cost_total || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" }) },
        ]} rows={payload?.trunk_summary || []} />;
      case "periodo":
        return (
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
            <HorizontalBars title="Por hora" rows={payload?.period_hourly || []} />
            <HorizontalBars title="Por dia" rows={payload?.period_daily || []} />
            <HorizontalBars title="Por dia da semana" rows={payload?.period_weekdays || []} />
          </div>
        );
      case "duracao":
        return <HorizontalBars title="Distribuição de duração" rows={payload?.duration_distribution || []} />;
      case "atendimento":
        return (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-3">
            <MiniCard icon={Clock3} label="Tempo médio" value={fmtDuration(payload?.answer_time?.avg_sec || 0)} />
            <MiniCard icon={Clock3} label="Tempo máximo" value={fmtDuration(payload?.answer_time?.max_sec || 0)} />
            <MiniCard icon={Clock3} label="Tempo mínimo" value={fmtDuration(payload?.answer_time?.min_sec || 0)} />
            <MiniCard icon={CheckCircle2} label="Atendidas até 20s" value={`${payload?.answer_time?.within_20_sec || 0}%`} />
            <MiniCard icon={CheckCircle2} label="Atendidas até 30s" value={`${payload?.answer_time?.within_30_sec || 0}%`} />
          </div>
        );
      case "falhas":
        return <DataTable columns={[
          { key: "label", label: "Falha / causa SIP" },
          { key: "count", label: "Quantidade", className: "font-mono" },
        ]} rows={payload?.sip_failures || []} />;
      case "simultaneidade":
        return (
          <div className="space-y-4">
            <MiniCard icon={PhoneCall} label="Pico de chamadas simultâneas" value={payload?.simultaneity?.peak || 0} />
            <DataTable
              columns={[
                { key: "timestamp", label: "Momento", render: (row) => fmtDate(row.timestamp), className: "font-mono text-xs" },
                { key: "simultaneous", label: "Simultâneas", className: "font-mono" },
              ]}
              rows={payload?.simultaneity?.timeline || []}
              emptyMessage="Sem eventos de simultaneidade no período."
            />
          </div>
        );
      case "gravacoes":
        return <DataTable columns={[
          { key: "started_at", label: "Data / Hora", render: (row) => fmtDate(row.started_at), className: "font-mono text-xs" },
          { key: "src", label: "Origem", className: "font-mono" },
          { key: "dst", label: "Destino", className: "font-mono" },
          { key: "queue_number", label: "Fila" },
          { key: "agent", label: "Agente" },
          { key: "duration", label: "Duração", render: (row) => fmtDuration(row.duration), className: "font-mono" },
          { key: "size_bytes", label: "Tamanho", render: (row) => `${((Number(row.size_bytes || 0)) / 1024 / 1024).toFixed(2)} MB`, className: "font-mono" },
          { key: "actions", label: "Ações", render: (row) => <RecordingActions row={row} /> },
        ]} rows={payload?.recordings || []} />;
      default:
        return null;
    }
  };

  return (
    <div className="space-y-5" data-testid="cdr-page">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-[color:var(--text-muted)]">Relatório operacional</div>
          <h1 className="text-2xl font-semibold tracking-tight">Relatório de Ligações</h1>
          <div className="text-sm text-[color:var(--text-secondary)] mt-1">
            CDR geral e detalhado com indicadores, gravações, falhas SIP e filtros por tenant, período, DID, ramal, tronco, direção e status.
          </div>
        </div>
        <div className="flex flex-wrap justify-end gap-2">
          <button className="sbc-btn" onClick={() => exportFile("csv")}><Download size={14} /> CSV</button>
          <button className="sbc-btn" onClick={() => exportFile("xlsx")}><FileSpreadsheet size={14} /> Excel</button>
          <button className="sbc-btn" onClick={() => exportFile("pdf")}><FileText size={14} /> PDF</button>
          <button className="sbc-btn sbc-btn-primary" onClick={() => load()} disabled={loading}>
            <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Atualizar
          </button>
        </div>
      </div>

      <div className="sbc-card p-4">
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-3">
          <label className="text-[10px] uppercase tracking-widest text-[color:var(--text-muted)]">
            Início
            <input type="date" className="sbc-input mt-1" value={draft.start} onChange={(e) => setDraft((old) => ({ ...old, start: e.target.value }))} />
          </label>
          <label className="text-[10px] uppercase tracking-widest text-[color:var(--text-muted)]">
            Fim
            <input type="date" className="sbc-input mt-1" value={draft.end} onChange={(e) => setDraft((old) => ({ ...old, end: e.target.value }))} />
          </label>
          <label className="text-[10px] uppercase tracking-widest text-[color:var(--text-muted)]">
            DID
            <select className="sbc-input mt-1 font-mono" value={draft.did} onChange={(e) => setDraft((old) => ({ ...old, did: e.target.value }))}>
              <option value="">Todos os DIDs do cliente</option>
              {dids.map((row) => (
                <option key={row.id || row.numero} value={row.numero}>
                  {row.numero}{row.descricao ? ` - ${row.descricao}` : ""}
                </option>
              ))}
            </select>
          </label>
          <label className="text-[10px] uppercase tracking-widest text-[color:var(--text-muted)]">
            Ramal
            <select className="sbc-input mt-1 font-mono" value={draft.extension} onChange={(e) => setDraft((old) => ({ ...old, extension: e.target.value }))}>
              <option value="">Todos os ramais do cliente</option>
              {extensions.map((row) => (
                <option key={row.id || row.numero} value={row.numero}>
                  {row.numero}{row.nome ? ` - ${row.nome}` : ""}
                </option>
              ))}
            </select>
          </label>
          <label className="text-[10px] uppercase tracking-widest text-[color:var(--text-muted)]">
            Tronco
            <select className="sbc-input mt-1" value={draft.trunk} onChange={(e) => setDraft((old) => ({ ...old, trunk: e.target.value }))}>
              <option value="">Todos os troncos do cliente</option>
              {trunks.map((row) => (
                <option key={row.id || row.nome || row.host} value={row.nome || row.host || ""}>
                  {row.nome || row.host || row.id}
                </option>
              ))}
            </select>
          </label>
          <label className="text-[10px] uppercase tracking-widest text-[color:var(--text-muted)]">
            Origem
            <input className="sbc-input mt-1 font-mono" value={draft.src} onChange={(e) => setDraft((old) => ({ ...old, src: e.target.value }))} placeholder="número de origem" />
          </label>
          <label className="text-[10px] uppercase tracking-widest text-[color:var(--text-muted)]">
            Destino
            <input className="sbc-input mt-1 font-mono" value={draft.dst} onChange={(e) => setDraft((old) => ({ ...old, dst: e.target.value }))} placeholder="número de destino" />
          </label>
          <label className="text-[10px] uppercase tracking-widest text-[color:var(--text-muted)]">
            Direção
            <select className="sbc-input mt-1" value={draft.direction} onChange={(e) => setDraft((old) => ({ ...old, direction: e.target.value }))}>
              <option value="">Todas</option>
              <option value="inbound">Entrada</option>
              <option value="outbound">Saída</option>
              <option value="internal">Interna</option>
            </select>
          </label>
          <label className="text-[10px] uppercase tracking-widest text-[color:var(--text-muted)]">
            Status
            <select className="sbc-input mt-1" value={draft.status} onChange={(e) => setDraft((old) => ({ ...old, status: e.target.value }))}>
              <option value="">Todos</option>
              <option value="completed">Completadas</option>
              <option value="not_completed">Não completadas</option>
              <option value="ANSWERED">ANSWERED</option>
              <option value="NO ANSWER">NO ANSWER</option>
              <option value="BUSY">BUSY</option>
              <option value="FAILED">FAILED</option>
              <option value="CONGESTION">CONGESTION</option>
            </select>
          </label>
          <label className="text-[10px] uppercase tracking-widest text-[color:var(--text-muted)] xl:col-span-2">
            Busca geral
            <div className="relative mt-1">
              <Search size={14} className="absolute left-3 top-3 text-[color:var(--text-muted)]" />
              <input className="sbc-input pl-9" value={draft.search} onChange={(e) => setDraft((old) => ({ ...old, search: e.target.value }))} placeholder="Call-ID, DID, CID, número, tronco ou ramal" />
            </div>
          </label>
        </div>
        <div className="flex flex-wrap justify-end gap-2 mt-4">
          <button className="sbc-btn" onClick={clearFilters}>Limpar</button>
          <button className="sbc-btn sbc-btn-primary" onClick={applyFilters} disabled={loading}>
            <Search size={14} /> Aplicar filtros
          </button>
        </div>
      </div>

      <nav className="flex gap-1 overflow-x-auto border-b border-[color:var(--border-default)] pb-2">
        {REPORT_TABS.map(([key, label]) => (
          <button key={key} className={`sbc-btn whitespace-nowrap ${activeTab === key ? "sbc-btn-primary" : ""}`} onClick={() => setActiveTab(key)}>
            {label}
          </button>
        ))}
      </nav>

      {error ? <div className="sbc-card p-3 text-xs text-[color:var(--accent-red)]">{error}</div> : null}
      {renderTab()}
    </div>
  );
}
